import { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, launchMspPortal } from "../../context/AuthContext";
import { InstanceContext, getCurrentInstance } from "../../context/InstanceContext";
import { fetchMspInstanceDetailsAsync } from "../../services/instance";
import styles from "./index.module.css";
import { Ring } from "@uiball/loaders";

const CheckoutSuccess: FC = () => {
  const navigate = useNavigate();

  const { instances, currentInstanceDetails, updateCurrentInstance, loading } = useContext(InstanceContext);
  const curInstance = getCurrentInstance(instances);
  const curInstanceId = curInstance?.id;

  const authInfo = useContext(AuthContext);

  const [polling, setPolling] = useState(false);
  const [pollFailed, setPollFailed] = useState(false);

  useEffect(() => {
    if (!curInstanceId || currentInstanceDetails || loading) return;
    const instanceId = curInstanceId;

    let cancelled = false;
    const MAX_RETRY = 5;
    const RETRY_INTERVAL_MS = 2000;

    async function pollInstance() {
      try {
        setPolling(true);
        setPollFailed(false);
        for (let i = 0; i < MAX_RETRY && !cancelled; i++) {
          try {
            const details = await fetchMspInstanceDetailsAsync(instanceId);
            if (!cancelled && details?.id) {
              updateCurrentInstance(details);
              return;
            }
          } catch (err) {
            console.error(err);
          }
          await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL_MS));
        }
        if (!cancelled) {
          setPollFailed(true);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) {
          setPolling(false);
        }
      }
    }

    pollInstance();

    return () => {
      cancelled = true;
    };
  }, [curInstanceId, currentInstanceDetails, updateCurrentInstance, loading]);

  if (polling) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: 48 }}>
        <Ring size={60} />
      </div>
    );
  }

  if (!currentInstanceDetails || pollFailed) {
    return (
      <div style={{ textAlign: "center", fontSize: 20, lineHeight: "100px" }}>
        No instance found.
      </div>
    );
  }

  if (currentInstanceDetails.domain === "*") {
    navigate("/specify-domain", { replace: true });
    return null;
  }

  return (
    <div className={styles["container"]}>
      <div className={styles["content"]}>
        <h2>
          Congratulations! You are now on the
          <span style={{ margin: "0px 8px" }}>
            {currentInstanceDetails.plan.category === "personal"
              ? "Professional"
              : "Business"}
          </span>
          Plan.
        </h2>
        <h3>
          Your MSP portal
          <span
            style={{ margin: "0.3em", textDecoration: "underline", cursor: "pointer" }}
            onClick={() => {
              launchMspPortal(currentInstanceDetails.domain, "_blank")
            }}
          >
            {currentInstanceDetails.domain}
          </span>
          will be ready in around 10 mins.
        </h3>
        <img src="/images/checkout-success.svg" alt="checkout success" />
        <p className={styles["more-info"]}>
          We'll send an email to
          <span className={styles["email"]}>{authInfo.email}</span>
          with a link to your account once everything is set up. In the
          meantime, feel free to check out
          <a
            href="https://help.firewalla.com/hc/en-us/articles/4409866753427"
            target="_blank"
            rel="noreferrer"
          >
            our article
          </a>
          on Firewalla MSP's key features and capabilities.
        </p>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
